<template>
  <v-card>
    <v-card-title>
      <v-icon left>mdi-content-save</v-icon>
      {{ editMode ? 'Update DG Billing Record' : 'Save DG Billing Record' }}
    </v-card-title>

    <v-card-text>
      <!-- Save Confirmation -->
      <v-row v-if="!saved">
        <v-col cols="12">
          <v-alert
            type="info"
            outlined
            class="mb-4"
          >
            <div class="text-subtitle-2">{{ editMode ? 'Ready to Update' : 'Ready to Save' }}</div>
            <div>
              Review the DG billing information below and click "{{
                editMode ? 'Update DG Record' : 'Save DG Record'
              }}" to persist the data.
            </div>
          </v-alert>

          <!-- DG Record Summary for Confirmation -->
          <v-card outlined class="mb-4">
            <v-card-title class="subtitle-1">
              <v-icon left>mdi-clipboard-check</v-icon>
              DG Billing Record Summary
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="primary">mdi-calendar-range</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Billing Period</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedBillingPeriod }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="success">mdi-lightning-bolt</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>DG Power Generation</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ formattedDGConsumption }} kWh
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="warning">mdi-gas-station</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Diesel Consumption</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ formattedDieselConsumption }} L
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="6">
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="info">mdi-clock</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Running Hours</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedRunningHours }} hrs</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="primary">mdi-gauge</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Power Efficiency</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedEfficiency }} kWh/L</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>

                    <v-list-item>
                      <v-list-item-icon>
                        <v-icon color="secondary">mdi-speedometer</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Fuel Rate</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedFuelRate }} L/hr</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <!-- DG Data Validation Status -->
          <v-row>
            <v-col cols="12">
              <v-card outlined :color="validationStatusColor">
                <v-card-title class="subtitle-2">
                  <v-icon left :color="validationStatusIconColor">
                    {{ validationStatusIcon }}
                  </v-icon>
                  DG Data Validation Status
                </v-card-title>

                <v-card-text>
                  <v-list dense>
                    <v-list-item v-for="validation in validationResults" :key="validation.field">
                      <v-list-item-icon>
                        <v-icon :color="validation.isValid ? 'success' : 'error'">
                          {{ validation.isValid ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                        </v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>{{ validation.field }}</v-list-item-title>
                        <v-list-item-subtitle>{{ validation.message }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>

      <!-- Success Message -->
      <v-row v-if="saved">
        <v-col cols="12">
          <v-alert
            type="success"
            outlined
            class="mb-4"
          >
            <div class="text-subtitle-2">
              <v-icon left>mdi-check-circle</v-icon>
              DG Billing Record Saved Successfully
            </div>
            <div>Record ID: {{ savedRecordId }}</div>
            <div>Saved at: {{ formattedSaveTime }}</div>
          </v-alert>

          <v-card outlined>
            <v-card-title class="subtitle-1">
              <v-icon left color="success">mdi-database-check</v-icon>
              Saved DG Record Details
            </v-card-title>

            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <div class="text-subtitle-2">Record Information</div>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Record ID</v-list-item-title>
                        <v-list-item-subtitle>{{ savedRecordId }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Created By</v-list-item-title>
                        <v-list-item-subtitle>
                          {{ currentDGData?.createdBy || 'operator' }}
                        </v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Save Time</v-list-item-title>
                        <v-list-item-subtitle>{{ formattedSaveTime }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>

                <v-col cols="12" md="6">
                  <div class="text-subtitle-2">Performance Summary</div>
                  <v-list dense>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Efficiency Rating</v-list-item-title>
                        <v-list-item-subtitle>{{ efficiencyRating }}</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Data Quality</v-list-item-title>
                        <v-list-item-subtitle>Validated and Verified</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item>
                      <v-list-item-content>
                        <v-list-item-title>Backup Status</v-list-item-title>
                        <v-list-item-subtitle>Automatically Backed Up</v-list-item-subtitle>
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Error Display -->
      <v-row v-if="error">
        <v-col cols="12">
          <v-alert
            type="error"
            dismissible
            @input="error = null"
          >
            <div class="text-subtitle-2">Save Failed</div>
            <div>{{ error }}</div>
          </v-alert>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions v-if="!saved">
      <v-spacer />
      <v-btn
        color="grey"
        text
        @click="cancel"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="saving"
        :disabled="!isValidForSave"
        @click="handleSaveClick"
      >
        <v-icon left>mdi-content-save</v-icon>
        {{ editMode ? 'Update DG Record' : 'Save DG Record' }}
      </v-btn>
    </v-card-actions>

    <v-card-actions v-if="saved">
      <v-spacer />
      <v-btn
        color="info"
        @click="viewRecord"
      >
        <v-icon left>mdi-eye</v-icon>
        View Record
      </v-btn>
      <v-btn
        color="success"
        @click="createNew"
      >
        <v-icon left>mdi-plus</v-icon>
        Create New DG Bill
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import appConfig from '../../config/appConfig';

export default {
  name: 'DGBillSaveComponent',

  props: {
    editMode: {
      type: Boolean,
      default: false,
    },
    originalRecord: {
      type: Object,
      default: null,
    },
    updatedRecord: {
      type: Object,
      default: null,
    },
    dgConsumptionData: {
      type: Object,
      default: null,
    },
    manualData: {
      type: Object,
      default: null,
    },
    efficiencyMetrics: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      saving: false,
      saved: false,
      savedRecordId: null,
      saveTime: null,
      error: null,
    };
  },

  computed: {
    // Get the appropriate data source based on mode
    currentDGData() {
      if (this.editMode && this.updatedRecord) {
        return this.updatedRecord;
      }
      return {
        billingPeriod: this.dgConsumptionData?.billingPeriod,
        dgConsumptionData: this.dgConsumptionData,
        dieselConsumption: this.manualData?.dieselConsumption || 0,
        runningHours: this.manualData?.runningHours || 0,
        efficiencyMetrics: this.efficiencyMetrics,
      };
    },

    // Formatting
    formattedBillingPeriod() {
      const data = this.currentDGData;
      if (!data?.billingPeriod) return 'N/A';
      const start = new Date(data.billingPeriod.startDate).toLocaleDateString();
      const end = new Date(data.billingPeriod.endDate).toLocaleDateString();
      return `${start} - ${end}`;
    },

    formattedDGConsumption() {
      const data = this.currentDGData;
      return data?.dgConsumptionData?.totalConsumption?.toLocaleString() || '0';
    },

    formattedDieselConsumption() {
      const data = this.currentDGData;
      return data?.dieselConsumption?.toLocaleString() || '0';
    },

    formattedRunningHours() {
      const data = this.currentDGData;
      return data?.runningHours?.toLocaleString() || '0';
    },

    formattedEfficiency() {
      const data = this.currentDGData;
      return data?.efficiencyMetrics?.kwhPerLitre?.toFixed(2) || '0.00';
    },

    formattedFuelRate() {
      const data = this.currentDGData;
      return data?.efficiencyMetrics?.litresPerHour?.toFixed(2) || '0.00';
    },

    formattedSaveTime() {
      return this.saveTime ? new Date(this.saveTime).toLocaleString() : 'N/A';
    },

    efficiencyRating() {
      const efficiency = parseFloat(this.formattedEfficiency);
      if (efficiency >= 3.5) return 'Excellent';
      if (efficiency >= 3.0) return 'Good';
      if (efficiency >= 2.5) return 'Average';
      return 'Poor';
    },

    // DG-specific validation
    validationResults() {
      const results = [];
      const data = this.currentDGData;

      // Billing period validation
      if (data?.billingPeriod && data.billingPeriod.startDate && data.billingPeriod.endDate) {
        results.push({
          field: 'Billing Period',
          isValid: true,
          message: 'Valid date range provided',
        });
      } else {
        results.push({
          field: 'Billing Period',
          isValid: false,
          message: 'Missing or invalid billing period',
        });
      }

      // DG consumption data validation
      if (data?.dgConsumptionData && data.dgConsumptionData.totalConsumption > 0) {
        results.push({
          field: 'DG Power Generation',
          isValid: true,
          message: 'Valid DG consumption data available',
        });
      } else {
        results.push({
          field: 'DG Power Generation',
          isValid: false,
          message: 'Missing or invalid DG consumption data',
        });
      }

      // Diesel consumption validation
      if (typeof data?.dieselConsumption === 'number' && data.dieselConsumption > 0) {
        results.push({
          field: 'Diesel Consumption',
          isValid: true,
          message: 'Valid diesel consumption value',
        });
      } else {
        results.push({
          field: 'Diesel Consumption',
          isValid: false,
          message: 'Missing or invalid diesel consumption',
        });
      }

      // Running hours validation
      if (typeof data?.runningHours === 'number' && data.runningHours > 0) {
        results.push({
          field: 'Running Hours',
          isValid: true,
          message: 'Valid running hours value',
        });
      } else {
        results.push({
          field: 'Running Hours',
          isValid: false,
          message: 'Missing or invalid running hours',
        });
      }

      // Efficiency metrics validation
      if (data?.efficiencyMetrics && data.efficiencyMetrics.kwhPerLitre > 0) {
        results.push({
          field: 'Efficiency Metrics',
          isValid: true,
          message: 'Efficiency calculations completed successfully',
        });
      } else {
        results.push({
          field: 'Efficiency Metrics',
          isValid: false,
          message: 'Missing or invalid efficiency metrics',
        });
      }

      return results;
    },

    isValidForSave() {
      return this.validationResults.every((result) => result.isValid);
    },

    validationStatusColor() {
      return this.isValidForSave ? 'success' : 'error';
    },

    validationStatusIcon() {
      return this.isValidForSave ? 'mdi-check-circle' : 'mdi-alert-circle';
    },

    validationStatusIconColor() {
      return this.isValidForSave ? 'success' : 'error';
    },
  },

  methods: {
    ...mapActions('dgBilling', ['saveDGBillingRecord', 'updateDGBillingRecord']),

    handleSaveClick() {
      this.handleSave();
    },

    async handleSave() {
      if (!this.isValidForSave) {
        this.error = 'Cannot save: DG data validation failed';
        return;
      }

      this.saving = true;
      this.error = null;

      try {
        let savedRecord;

        if (this.editMode) {
          // Update existing DG record
          const recordId = this.originalRecord._id || this.originalRecord.id;
          savedRecord = await this.updateDGBillingRecord({
            id: recordId,
            recordData: this.updatedRecord,
          });

          this.$emit('save-success', savedRecord);
          this.$emit('show-message', {
            type: 'success',
            message: 'DG billing record updated successfully',
          });
        } else {
          // Create new DG record
          const recordData = {
            billingPeriod: this.dgConsumptionData.billingPeriod,
            dgConsumptionData: {
              totalConsumption: this.dgConsumptionData.totalConsumption,
              initialReading: this.dgConsumptionData.initialReading,
              finalReading: this.dgConsumptionData.finalReading,
              billingPeriod: this.dgConsumptionData.billingPeriod,
            },
            dieselConsumption: this.manualData.dieselConsumption,
            runningHours: this.manualData.runningHours,
            efficiencyMetrics: this.efficiencyMetrics,
            notes: this.manualData.notes || '',
            createdBy: this.manualData.createdBy || appConfig.getParentUserName(),
          };

          // Additional validation before saving - only for manual mode
          // In automatic mode, initialReading and finalReading are null, so skip this validation
          const isManualMode = recordData.dgConsumptionData.initialReading !== null
            && recordData.dgConsumptionData.finalReading !== null;

          if (
            isManualMode
            && recordData.dgConsumptionData.finalReading
              < recordData.dgConsumptionData.initialReading
          ) {
            throw new Error('Final reading must be greater than or equal to initial reading');
          }

          savedRecord = await this.saveDGBillingRecord(recordData);

          this.$emit('record-saved', savedRecord);
          this.$emit('show-message', {
            type: 'success',
            message: `DG billing record saved successfully with ID: ${savedRecord.id}`,
          });
        }

        this.savedRecordId = savedRecord.id || savedRecord._id;
        this.saveTime = savedRecord.updatedAt || savedRecord.createdAt;
        this.saved = true;
      } catch (error) {
        console.error('Error saving DG billing record:', error);
        this.error = error.message || 'Failed to save DG billing record';

        this.$emit('save-error', error);
        this.$emit('show-message', {
          type: 'error',
          message: this.error,
        });
      } finally {
        this.saving = false;
      }
    },

    cancel() {
      this.$emit('save-cancelled');
    },

    viewRecord() {
      if (this.savedRecordId) {
        this.$emit('view-record', this.savedRecordId);
      }
    },

    createNew() {
      this.$emit('create-new-dg-bill');
    },

    // Public method to reset the component
    reset() {
      this.saving = false;
      this.saved = false;
      this.savedRecordId = null;
      this.saveTime = null;
      this.error = null;
    },
  },
};
</script>

<style scoped>
.v-list-item__subtitle {
  white-space: normal !important;
}
</style>
